import React from 'react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Typography from '@mui/material/Typography';

const symbols = [
  { symbol: '>', label: 'Büyüktür' },
  { symbol: '<', label: 'Küçüktür' },
  { symbol: '=', label: 'Eşittir' },
];

export function comparisonSymbol(value) {
  const text = String(value ?? '').trim();
  if (text === '>' || text === '<' || text === '=') return text;
  const label = text.toLocaleLowerCase('tr-TR');
  const namedSymbols = { büyüktür: '>', küçüktür: '<', eşittir: '=', eşit: '=' };
  return namedSymbols[label] || /^(?:büyüktür|küçüktür|eşittir|eşit)\s*\(([><=])\)$/.exec(label)?.[1] || null;
}

export function comparisonQuestion(question) {
  const choices = question?.choices || [];
  const answers = symbols.map(({ symbol }) => choices.find((choice) => comparisonSymbol(choice) === symbol));
  if (answers.some((answer) => answer == null)) return null;
  const prompt = String(question?.prompt || '').trim();
  const match = /^(.+?)\s+sayısı\s+(.+?)\s+sayısına göre nasıldır\?\s*$/i.exec(prompt)
    || /^(?:Karşılaştır(?:ınız)?\s*:\s*)?(.+?)\s*(?:_+|\.{2,}|…|□|\?)\s*(.+?)(?:\s+boşlu[ğk].*)?$/.exec(prompt);
  if (!match) return null;
  // Only two numeric quantities: do not turn ordering or inequality problems into a single blank.
  const quantity = /^[+−-]?\d[\d\s.,/]*%?$/;
  if (!quantity.test(match[1].trim()) || !quantity.test(match[2].trim())) return null;
  if (question?.correct_answer != null && !comparisonSymbol(question.correct_answer)) return null;
  return { left: match[1], right: match[2], answers };
}

export default function ComparisonAnswerInput({ comparison, selectedChoice, correctAnswer, reveal, disabled, onAnswer }) {
  const selectedSymbol = comparisonSymbol(selectedChoice);
  const correctSymbol = comparisonSymbol(correctAnswer);
  const shownSymbol = reveal ? correctSymbol : selectedSymbol;
  const longNumbers = Math.max(comparison.left.length, comparison.right.length) > 8;
  return (
    <Box sx={{ textAlign: 'center', position: 'relative', zIndex: 1, mb: 3 }}>
      <Typography sx={{ fontSize: { xs: 20, md: 28 }, fontWeight: 900, color: '#6d5d93', mb: 2 }}>Doğru sembole dokun, boşluğu doldur.</Typography>
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: { xs: longNumbers ? 0.7 : 1.5, md: 3 }, mb: 4, fontWeight: 1000, fontSize: longNumbers ? { xs: 'clamp(14px, 3.5vw, 24px)', sm: 30, md: 48 } : { xs: 36, sm: 54, md: 76 } }}>
        <Box sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>{comparison.left}</Box>
        <Box aria-label="Karşılaştırma boşluğu" data-testid="comparison-slot" sx={{ width: { xs: 70, md: 110 }, height: { xs: 78, md: 120 }, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 3, border: `3px ${shownSymbol ? 'solid' : 'dashed'} ${reveal ? '#22c55e' : '#a78bfa'}`, bgcolor: reveal ? '#f0fdf4' : '#f5f0ff', color: reveal ? '#15803d' : '#33258a', transition: 'background-color 200ms, border-color 200ms' }}>{shownSymbol || '?'}</Box>
        <Box sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>{comparison.right}</Box>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: { xs: 2, md: 4 } }}>
        {symbols.map(({ symbol, label }, index) => (
          <ButtonBase key={symbol} aria-label={label} disabled={disabled} onClick={() => onAnswer(comparison.answers[index])} sx={{ width: { xs: 76, sm: 100, md: 120 }, height: { xs: 76, sm: 100, md: 120 }, borderRadius: '50%', border: '3px solid #c4b5fd', bgcolor: '#f5f0ff', color: '#33258a', fontWeight: 1000, fontSize: { xs: 42, md: 64 }, boxShadow: '0 6px 0 #ddd6fe', '&:hover': { bgcolor: '#ede9fe' }, '&:active': { transform: 'translateY(4px)', boxShadow: '0 2px 0 #ddd6fe' }, '&.Mui-disabled': { opacity: 0.55 } }}>{symbol}</ButtonBase>
        ))}
      </Box>
    </Box>
  );
}
